import { Card, Chip, Menu, MenuItem, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SelectableDocumentFormats } from 'src/configuration/documents';
import { useResource } from 'src/hooks/api';
import { useUserStore } from 'src/hooks/zustand/user';
import { useNewspaperStore } from '../../hooks/zustand/newspapers';
import * as AssignmentsAPI from '../../api/Assignments';
import * as DocumentsAPI from '../../api/Documents';
import * as NewspapersAPI from '../../api/Newspapers';
import { Edition, STORY_TEMPLATE_LABELS, StoryTemplate } from '../../types/models/Newspaper';
import CreateNewspaperDialog from '../../components/dialogs/CreateNewspaperDialog';
import NewTeacherWizard from '../../components/dialogs/NewTeacherWizard';
import { useClassroomStore } from '../../hooks/zustand/classrooms';
import { Chevron } from '../../assets/icons/variantComponents';
import AssignmentButton from '../../components/AssignmentButton';
import PresstoButton from '../../components/PresstoButton';
import { lang } from '../../lang';
import AlignmentInput from './AlignmentInput';
import { PresstoGrid } from './styles';
import BaseDialog, {
  DialogContent,
  DialogTitle,
} from '../../components/dialogs/BaseDialog';

const SORT_BY = ['recent', 'oldest', 'az', 'za'] as const;

export default function GalleryTab() {
  // const documents = useDocumentStore((state) => state.documents);
  const user = useUserStore((state) => state.user);
  const navigate = useNavigate();
  const newspapers = useNewspaperStore((state) => state.newspapers);
  const loadNewspapers = useNewspaperStore((state) => state.loadNewspapers);

  const [studentEditions, setStudentEditions] = useState<(Edition & {
    newspaper_name: string;
    newspaper_id: string;
    my_story?: {
      id: string;
      template_type: string;
      title: string;
      document_id: string | null;
      status: string;
    } | null;
  })[]>([]);

  useEffect(() => {
    if (user.role === 'teacher') loadNewspapers();
    if (user.role === 'student') {
      NewspapersAPI.getMyEditions().then((res) => {
        const editions = res.data.data ?? res.data;
        setStudentEditions(editions as any);
      }).catch(() => {
      });
    }
  }, []);

  const classrooms = useClassroomStore((state) => state.classrooms);
  const loadClassrooms = useClassroomStore((state) => state.loadClassrooms);
  const [isCreateNewspaperOpen, setIsCreateNewspaperOpen] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  useEffect(() => {
    if (user.role === 'teacher') loadClassrooms();
  }, []);
  const [sortedBy, setSortedBy] = useState<string>(SORT_BY[0]);
  const documentParams = useMemo(
    () => ({
      order: sortedBy,
    }),
    [sortedBy],
  );

  const theme = useTheme();
  const mediaSm = useMediaQuery(theme.breakpoints.up('sm'));
  const mediaMd = useMediaQuery(theme.breakpoints.up('md'));
  const mediaLg = useMediaQuery(theme.breakpoints.up('lg'));
  const mediaXl = useMediaQuery(theme.breakpoints.up('xl'));

  const [search, setSearch] = useSearchParams();

  const documents = useResource(DocumentsAPI.getAll, documentParams);
  const assignments = useResource(AssignmentsAPI.getAll);

  // Build a lookup: documentId → edition data (for rendering newspaper docs correctly)
  // Only map open editions — drafts and published editions shouldn't render as active cards.
  const docToEdition = new Map<string, any>();
  studentEditions.forEach((ed) => {
    if (ed.my_story?.document_id && ed.status === 'open') {
      docToEdition.set(ed.my_story.document_id, ed);
    }
  });

  // Collect edition IDs that are already represented by a document (to avoid duplicates)
  const editionIdsWithDocs = new Set(
    studentEditions.filter((e) => e.my_story?.document_id).map((e) => e.id),
  );

  const typeFilter = search.get('type');
  const cards = [
    ...(documents?.map((document) => {
      const linkedEdition = docToEdition.get(document.id);
      if (linkedEdition) {
        // This document belongs to a newspaper story — render as edition card
        return { type: 'edition' as const, ...linkedEdition };
      }
      return { type: 'document' as const, ...document };
    }) || []),
    ...(assignments?.map((assignment) => ({
      type: 'assignment' as const,
      ...assignment,
    })) || []),
    ...(user.role === 'teacher' ? newspapers.map((newspaper) => ({
      type: 'newspaper' as const,
      ...newspaper,
    })) : []),
    // Only include editions the teacher has opened for assignment.
    // Also exclude editions already represented by a document.
    ...(user.role === 'student' ? studentEditions
      .filter((edition) => !editionIdsWithDocs.has(edition.id) && edition.status === 'open')
      .map((edition) => ({
        type: 'edition' as const,
        ...edition,
      })) : []),
  ].filter((card) => {
    if (typeFilter) {
      return card.type === typeFilter;
    }
    return true;
  });

  cards.sort((a, b) => {
    if (sortedBy === 'az') {
      return a.name?.localeCompare(b.name || '') || 1;
    }
    if (sortedBy === 'za') {
      return b.name?.localeCompare(a.name || '') || 1;
    }
    if (sortedBy === 'recent') {
      return b.created_at.localeCompare(a.created_at);
    }
    if (sortedBy === 'oldest') {
      return a.created_at.localeCompare(b.created_at);
    }
    return 0;
  });

  // Specify behavior of the sort widget.
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const sortedByLabel = useMemo(
    () =>
      lang(
        `gallery.sort_select.${
          SORT_BY.find((el) => el === sortedBy) ?? SORT_BY[0]
        }`,
      ),
    [sortedBy],
  );
  const handleOpenSortByDialog = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseSortByDialog = (selectedItem?: string) => {
    if (selectedItem) {
      setSortedBy(selectedItem);
      localStorage.setItem('sortDocuments', selectedItem);
    }
    setAnchorEl(null);
  };

  // Declare the templates to be shown. Templates are shortcuts
  // for creating documents.
  const formatFilter = SelectableDocumentFormats.find(
    (f) => f === search.get('templateFormat'),
  );

  let templatePageSize = 2;

  if (mediaSm) {
    templatePageSize = 3;
  }
  if (mediaMd) {
    templatePageSize = 4;
  }
  if (mediaLg) {
    templatePageSize = 5;
  }
  if (mediaXl) {
    templatePageSize = 6;
  }
  const templatePage = Number(search.get('page')) || 1;
  let begin = (templatePage - 1) * templatePageSize;

  // Pretend as though the '+ Blank' button is the first item in the
  // list.
  if (!formatFilter) {
    if (begin > 0) {
      begin -= 1;
    }
  }
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <div
      style={{
        overflowY: 'auto',
      }}
    >
      <BaseDialog
        onClose={() => {
          setIsDialogOpen(false);
        }}
        size="large"
        open={isDialogOpen}
      >
        <DialogTitle style={{ padding: '60px 75px 0 75px' }}>
          New Assignment
        </DialogTitle>
        <DialogContent style={{ paddingTop: '40px' }}>
          <AlignmentInput />
        </DialogContent>
      </BaseDialog>
      <PresstoGrid>
        <div>
          {/* Create section — teachers only */}
          {user.role === 'teacher' && (
            <div>
              <div style={{ margin: '2rem 0 1rem' }}>
                <strong>Create</strong>
              </div>
              <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
                {/* Start a Newspaper — primary action */}
                <Card
                  onClick={() => classrooms.length === 0 ? setIsWizardOpen(true) : setIsCreateNewspaperOpen(true)}
                  elevation={2}
                  sx={{
                    cursor: 'pointer',
                    width: '12.5rem',
                    height: 110,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 !important',
                    background: '#faf7f0',
                    color: '#1a1a1a',
                    border: '2px solid #d4c9a8',
                    overflow: 'hidden',
                    transition: 'all 0.15s ease',
                    '&:hover': { background: '#f5f0e3', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', transform: 'translateY(-2px)' },
                  }}
                >
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    flex: 1,
                  }}>
                    <span style={{ fontSize: '1.5rem', fontWeight: 300, color: '#163567' }}>+</span>
                    <span style={{
                      fontFamily: '"Georgia", "Times New Roman", serif',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      color: '#163567',
                    }}>
                      Start a Newspaper
                    </span>
                  </div>
                </Card>

                {/* New Assignment */}
                <Card
                  onClick={() => setIsDialogOpen(true)}
                  elevation={2}
                  sx={{
                    cursor: 'pointer',
                    width: '12.5rem',
                    height: 110,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    color: '#163567',
                    border: '2px dashed #c0c0c0',
                    background: 'transparent',
                    transition: 'all 0.15s ease',
                    '&:hover': { borderColor: '#163567', background: '#f0f0f4', transform: 'translateY(-2px)' },
                  }}
                >
                  <span style={{ fontSize: '1.5rem', fontWeight: 300 }}>+</span>
                  <Typography variant="body2" fontWeight={600}>New Assignment</Typography>
                </Card>

                {/* New Classroom */}
                <Card
                  onClick={() => navigate('/gallery/classrooms')}
                  elevation={2}
                  sx={{
                    cursor: 'pointer',
                    width: '12.5rem',
                    height: 110,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    color: '#163567',
                    border: '2px dashed #c0c0c0',
                    background: 'transparent',
                    transition: 'all 0.15s ease',
                    '&:hover': { borderColor: '#163567', background: '#f0f0f4', transform: 'translateY(-2px)' },
                  }}
                >
                  <span style={{ fontSize: '1.5rem', fontWeight: 300 }}>+</span>
                  <Typography variant="body2" fontWeight={600}>New Classroom</Typography>
                </Card>

                {/* From a Template — opens the RGR-aligned lesson catalog */}
                <Card
                  onClick={() => navigate('/gallery/templates')}
                  elevation={2}
                  sx={{
                    cursor: 'pointer',
                    width: '12.5rem',
                    height: 110,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    color: '#163567',
                    border: '2px dashed #c0c0c0',
                    background: 'transparent',
                    transition: 'all 0.15s ease',
                    '&:hover': { borderColor: '#163567', background: '#f0f0f4', transform: 'translateY(-2px)' },
                  }}
                >
                  <span style={{ fontSize: '1.5rem', fontWeight: 300 }}>+</span>
                  <Typography variant="body2" fontWeight={600}>From a Template</Typography>
                </Card>
              </div>
            </div>
          )}

          <div className="pressto-count-row">
            <div className="pressto-count">
              <strong>Your Files</strong>
              <div className="filters">
                {[
                  { label: 'All', value: '' },
                  { label: 'Assignments', value: 'assignment' },
                  { label: 'Presstos', value: 'document' },
                  ...(user.role === 'teacher' ? [{ label: 'Newspapers', value: 'newspaper' }] : []),
                  ...(user.role === 'student' && studentEditions.length > 0 ? [{ label: 'Newspapers', value: 'edition' }] : []),
                ].map((type) => (
                  <Chip
                    key={type.value || 'all'}
                    label={type.label}
                    variant="filled"
                    onClick={() => {
                      const newSearch = new URLSearchParams(search);
                      if (type.value === '' || type.value === typeFilter) {
                        newSearch.delete('type');
                      } else {
                        newSearch.set('type', type.value);
                      }
                      setSearch(newSearch);
                    }}
                    color={type.value === typeFilter ? 'primary' : 'default'}
                  />
                ))}
              </div>
            </div>
            <div className="sorted-by">
              <span>{lang('general.organize_by')}</span>
              <u
                id="sort-by-button"
                aria-controls={open ? 'sorted-by-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                role="button"
                onClick={handleOpenSortByDialog}
                onKeyDown={handleOpenSortByDialog}
                tabIndex={-1}
                style={{ cursor: 'pointer' }}
              >
                {sortedByLabel} <Chevron direction={open ? 'up' : 'down'} />
              </u>
              <Menu
                id="sorted-by-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleCloseSortByDialog()}
                MenuListProps={{ 'aria-labelledby': 'sort-by-button' }}
                classes={{ paper: 'order-by-container', list: 'order-by-menu' }}
              >
                {SORT_BY.map((el) => (
                  <MenuItem
                    key={el}
                    onClick={() => handleCloseSortByDialog(el)}
                    style={el === sortedBy ? { fontWeight: 700 } : {}}
                  >
                    {lang(`gallery.sort_select.${el}`)}
                  </MenuItem>
                ))}
              </Menu>
            </div>
          </div>
          <div className="content">
            {cards?.map((pressto) => {
              if (pressto.type === 'assignment') {
                return (
                  <AssignmentButton
                    key={`pressto-${pressto.id}`}
                    assignment={pressto as any}
                    onDeleted={assignments?.$reload}
                  />
                );
              }
              if (pressto.type === 'newspaper') {
                const np = pressto as any;
                const submittedCount = np.submitted_stories_count ?? 0;
                return (
                  <Card
                    key={`newspaper-${pressto.id}`}
                    onClick={() => navigate(`/newspaper/${pressto.id}`)}
                    elevation={4}
                    className="pressto-button"
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      padding: '0 !important',
                      background: '#faf7f0 !important',
                      color: '#1a1a1a !important',
                      border: '1px solid #d4c9a8',
                      overflow: 'hidden',
                      position: 'relative',
                      '&:hover': { background: '#f5f0e3 !important', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
                    }}
                  >
                    {/* Submitted stories badge */}
                    {submittedCount > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        background: '#e65100',
                        color: '#fff',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}>
                        {submittedCount}
                      </div>
                    )}
                    <div style={{
                      borderBottom: '3px double #1a1a1a',
                      padding: '0.6rem 0.75rem 0.4rem',
                      textAlign: 'center',
                    }}>
                      <div style={{
                        fontFamily: '"Georgia", "Times New Roman", serif',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        lineHeight: 1.2,
                        letterSpacing: '0.02em',
                      }}>
                        {pressto.name}
                      </div>
                      <div style={{
                        fontSize: '0.55rem',
                        opacity: 0.5,
                        marginTop: '2px',
                        fontFamily: '"Georgia", serif',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                      }}>
                        {np.scope_type} edition
                      </div>
                    </div>
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      gap: '6px',
                      padding: '0.5rem 0.75rem',
                    }}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ height: 4, background: '#ccc', borderRadius: 1 }} />
                        <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '80%' }} />
                        <div style={{ height: 4, background: '#ccc', borderRadius: 1 }} />
                        <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '60%' }} />
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '70%' }} />
                        <div style={{ height: 4, background: '#ccc', borderRadius: 1 }} />
                        <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '90%' }} />
                        <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '50%' }} />
                      </div>
                    </div>
                    <div style={{
                      borderTop: '1px solid #d4c9a8',
                      padding: '0.35rem 0.75rem',
                      fontSize: '0.65rem',
                      color: '#888',
                      fontFamily: '"Georgia", serif',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}>
                      <span>{np.editions_count} {np.editions_count === 1 ? 'edition' : 'editions'}</span>
                      <span>{np.students_count ?? 0} {np.students_count === 1 ? 'student' : 'students'}</span>
                    </div>
                  </Card>
                );
              }
              if (pressto.type === 'edition') {
                const ed = pressto as any;
                const myStory = ed.my_story;
                const hasStory = myStory?.document_id;

                const handleClick = () => {
                  if (hasStory) {
                    // Go directly to the document
                    navigate(`/document/${myStory.document_id}/`);
                  } else {
                    // Go to template picker
                    navigate(`/newspaper/${ed.newspaper_id}/edition/${ed.id}`);
                  }
                };

                return (
                  <Card
                    key={`edition-${pressto.id}`}
                    onClick={handleClick}
                    elevation={4}
                    className="pressto-button"
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      padding: '0 !important',
                      background: '#faf7f0 !important',
                      color: '#1a1a1a !important',
                      border: hasStory ? '1px solid #a5d6a7' : '1px solid #d4c9a8',
                      overflow: 'hidden',
                      position: 'relative',
                      '&:hover': { background: '#f5f0e3 !important', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
                    }}
                  >
                    {/* New badge */}
                    {!hasStory && (
                      <div style={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        background: '#fff3e0',
                        color: '#e65100',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 4,
                        zIndex: 1,
                      }}>
                        New
                      </div>
                    )}
                    {/* Masthead */}
                    <div style={{
                      borderBottom: '3px double #1a1a1a',
                      padding: '0.6rem 0.75rem 0.4rem',
                      textAlign: 'center',
                      width: '100%',
                    }}>
                      <div style={{
                        fontFamily: '"Georgia", "Times New Roman", serif',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        lineHeight: 1.2,
                        letterSpacing: '0.02em',
                      }}>
                        {ed.newspaper_name}
                      </div>
                      <div style={{
                        fontSize: '0.65rem',
                        opacity: 0.7,
                        marginTop: '3px',
                        fontFamily: '"Georgia", serif',
                        letterSpacing: '0.05em',
                        fontStyle: 'italic',
                      }}>
                        {ed.name}
                      </div>
                    </div>
                    {/* Content area */}
                    {hasStory ? (
                      <div style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '0.75rem',
                        textAlign: 'center',
                      }}>
                        <div style={{
                          fontFamily: '"Georgia", serif',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          color: '#1a1a1a',
                        }}>
                          {myStory.title || STORY_TEMPLATE_LABELS[myStory.template_type as StoryTemplate] || myStory.template_type}
                        </div>
                        <Chip
                          label={STORY_TEMPLATE_LABELS[myStory.template_type as StoryTemplate] || myStory.template_type}
                          size="small"
                          sx={{ mt: 0.5, fontSize: '0.6rem', height: 18 }}
                          color="success"
                          variant="outlined"
                        />
                      </div>
                    ) : (
                      <div style={{
                        flex: 1,
                        display: 'flex',
                        gap: '6px',
                        padding: '0.5rem 0.75rem',
                        width: '100%',
                      }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ height: 4, background: '#ccc', borderRadius: 1 }} />
                          <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '80%' }} />
                          <div style={{ height: 4, background: '#ccc', borderRadius: 1 }} />
                          <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '60%' }} />
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '70%' }} />
                          <div style={{ height: 4, background: '#ccc', borderRadius: 1 }} />
                          <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '90%' }} />
                          <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '50%' }} />
                        </div>
                      </div>
                    )}
                    {/* Footer */}
                    <div style={{
                      borderTop: '1px solid #d4c9a8',
                      padding: '0.4rem 0.75rem',
                      fontFamily: '"Georgia", serif',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: myStory?.status === 'submitted' ? '#2e7d32' : hasStory ? '#163567' : '#163567',
                      textAlign: 'center',
                      width: '100%',
                    }}>
                      {hasStory
                        ? (myStory.status === 'submitted' ? 'Turned in \u2713' : 'Continue writing \u2192')
                        : 'Pick your story \u2192'}
                    </div>
                  </Card>
                );
              }
              return (
                <PresstoButton
                  key={pressto.id}
                  pressto={pressto as any}
                  onDeleted={documents?.$reload}
                />
              );
            })}
          </div>
        </div>
      </PresstoGrid>

      <CreateNewspaperDialog
        isOpen={isCreateNewspaperOpen}
        onClose={() => setIsCreateNewspaperOpen(false)}
      />

      <NewTeacherWizard
        isOpen={isWizardOpen}
        onClose={() => {
          setIsWizardOpen(false);
          loadNewspapers();
          loadClassrooms();
        }}
      />
    </div>
  );
}
